'use client'

import type { RefObject } from 'react'
import { useEffect, useState } from 'react'

import type { TVVisiblePriceRange, TVWidgetInstance } from '@/lib/tradingview/types/tv-widget.types'

const PRICE_RANGE_POLL_MS = 500

export type ChartPriceScaleState = {
  visiblePriceRange: TVVisiblePriceRange | null
  isInverted: boolean
  mainPaneBounds: ChartMainPaneBounds | null
}

export type ChartMainPaneBounds = {
  top: number
  height: number
}

const IDLE_STATE: ChartPriceScaleState = {
  visiblePriceRange: null,
  isInverted: false,
  mainPaneBounds: null,
}

function getMainPaneBounds(
  container: HTMLElement | null,
  paneHeight: number | undefined
): ChartMainPaneBounds | null {
  if (!container || !paneHeight || paneHeight <= 0) return null

  const iframe = container.querySelector('iframe')
  const iframeDocument = iframe?.contentDocument
  const chartArea = iframeDocument?.querySelector<HTMLElement>('.layout__area--center')
  if (!iframe || !chartArea) return null

  const containerRect = container.getBoundingClientRect()
  const iframeRect = iframe.getBoundingClientRect()
  const chartAreaRect = chartArea.getBoundingClientRect()

  return {
    top: iframeRect.top - containerRect.top + chartAreaRect.top,
    height: paneHeight,
  }
}

/**
 * Polls the chart's main price scale purely for RENDERING (e.g. projecting a fixed,
 * producer-owned bucket grid onto the chart's own price axis) — never to build a request. The
 * Volume/Liquidation/OI visible-range profile already polls the same underlying TV API
 * (use-visible-range-profile.ts) to build its HTTP request; this hook exists because the
 * liquidation/stop-level profiles take no price-range request field at all, so nothing else in
 * the app already surfaces this state for rendering-only consumers.
 */
export function useChartPriceScale({
  widget,
  isChartReady,
  enabled,
  containerRef,
}: {
  widget: TVWidgetInstance | null
  isChartReady: boolean
  enabled: boolean
  containerRef: RefObject<HTMLElement | null>
}): ChartPriceScaleState {
  const [state, setState] = useState<ChartPriceScaleState>(IDLE_STATE)

  useEffect(() => {
    if (!enabled || !isChartReady || !widget) {
      setState(IDLE_STATE)
      return
    }

    const chart = widget.activeChart()

    const capture = () => {
      try {
        const mainPane = chart.getPanes()[0]
        const priceScale = mainPane?.getMainSourcePriceScale()
        const visiblePriceRange = priceScale?.getVisiblePriceRange() ?? null
        const isInverted = priceScale?.isInverted() ?? false
        const mainPaneBounds = getMainPaneBounds(containerRef.current, mainPane?.getHeight?.())
        setState((current) => {
          const isUnchanged =
            current.visiblePriceRange?.from === visiblePriceRange?.from &&
            current.visiblePriceRange?.to === visiblePriceRange?.to &&
            current.isInverted === isInverted &&
            current.mainPaneBounds?.top === mainPaneBounds?.top &&
            current.mainPaneBounds?.height === mainPaneBounds?.height

          return isUnchanged ? current : { visiblePriceRange, isInverted, mainPaneBounds }
        })
      } catch {}
    }

    capture()
    const pollId = setInterval(capture, PRICE_RANGE_POLL_MS)
    return () => clearInterval(pollId)
  }, [containerRef, enabled, isChartReady, widget])

  return state
}
